import { queryOptions } from '@tanstack/react-query';
import {
  listScheduledMessages,
  listBookingAutomationMessages,
  cancelScheduledMessage,
  retryScheduledMessage,
} from '@/api/generated/sdk.gen';
import type {
  ScheduledMessageSummary,
  BookingAutomationMessage,
} from '@/api/generated/types.gen';

export type { ScheduledMessageSummary, BookingAutomationMessage };
export { cancelScheduledMessage, retryScheduledMessage };

// bookingAutomationMessagesQuery — every scheduled automation message for one
// booking, enriched with rule/template names and a body preview (booking Debug
// tab). Short staleTime: the operator flow is "look, act, look again".
export function bookingAutomationMessagesQuery(bookingId: string) {
  return queryOptions({
    queryKey: ['bookings', bookingId, 'automation-messages'] as const,
    queryFn: async ({ signal }) => {
      const res = await listBookingAutomationMessages({
        throwOnError: true,
        signal,
        path: { id: bookingId },
      });
      return res.data;
    },
    enabled: !!bookingId,
    staleTime: 5_000,
  });
}

export type ScheduledStatus = 'pending' | 'sent' | 'failed' | 'cancelled';

export interface ScheduledFilter {
  status?: ScheduledStatus;
  limit?: number;
  offset?: number;
}

export function scheduledQuery(filter: ScheduledFilter = {}) {
  return queryOptions({
    queryKey: ['scheduled', filter] as const,
    queryFn: async ({ signal }) => {
      const res = await listScheduledMessages({
        throwOnError: true,
        signal,
        query: {
          status: filter.status,
          limit: filter.limit ?? 50,
          offset: filter.offset ?? 0,
        },
      });
      return res.data;
    },
    retry: (count, err) => {
      const status = (err as { status?: number })?.status;
      return status !== 401 && status !== 404 && count < 1;
    },
    staleTime: 30_000,
  });
}
